import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useReduxAuth';
import { useNotification } from './useNotification';
import { useTranslation } from 'react-i18next';
import { hasuraClient } from '../utils/graphqlClient';
import {
  GET_GROUPS_FOR_FORM_QUERY,
  GET_ALL_GROUPS_FOR_FORM_QUERY,
  GET_COMPANIES_FOR_USER_FORM_QUERY,
  GET_ALL_USERS_QUERY,
} from '../graphql/queries';
import { UPDATE_TEAM_TAGS } from '../graphql/mutations';
import { useDynamicEntity } from './useDynamicEntity';

export const useTeams = ({ paginated = false } = {}) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { showNotification } = useNotification();

  const [groups, setGroups] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);

  const isRootUser = currentUser?.role === 'ROOT';
  const isGroupManager = currentUser?.role === 'GROUP_MANAGER';

  // GROUP_MANAGER için group_id filtresi
  const getCustomWhereCondition = () => {
    if (isRootUser) {
      return null;
    }
    
    if (isGroupManager) {
      const groupId = currentUser?.team?.group_id || currentUser?.group_id;
      if (groupId) {
        return {
          company_id: { _eq: currentUser?.companyId },
          group_id: { _eq: groupId }
        };
      }
      return { company_id: { _eq: currentUser?.companyId } };
    }
    
    return { company_id: { _eq: currentUser?.companyId } };
  };

  const dynamicHook = useDynamicEntity({
    entityType: 'teams',
    gqlEntityKey: 'teams',
    queryName: isRootUser ? 'GET_ALL_TEAMS_QUERY' : 'GET_TEAMS_BY_COMPANY_QUERY',
    createMutation: 'CREATE_TEAM_MUTATION',
    updateMutation: 'UPDATE_TEAM_MUTATION',
    deleteMutation: 'DELETE_TEAM_MUTATION',
    // Teams pattern configuration
    mutationPattern: 'teams',
    createDirectVariables: true,
    updateDirectVariables: true,
    serverPagination: paginated,
    aggregateKey: paginated ? 'teams_aggregate' : null,
    transformData: (team) => ({
        ...team,
        companyName: team.company?.name || 'Unknown Company',
        groupName: team.group?.name || 'Atanmamış',
        managerName: team.user?.first_name 
          ? `${team.user.first_name} ${team.user.last_name}`
          : 'Atanmamış',
        memberCount: team.users?.length || 0,
        firmCount: team.team_firms?.length || 0,
    }),
    transformFormData: (formData) => ({
      name: formData.name,
      description: formData.description || null,
      group_id: formData.groupId || null,
      company_id: formData.companyId || currentUser?.companyId,
      team_manager_id: formData.teamManagerId || null,
    }),
    transformFormDataForEdit: (team) => {
      return {
        name: team.name,
        description: team.description,
        companyId: team.company_id,
        groupId: team.group_id,
        teamManagerId: team.team_manager_id,
        tagIds: team.team_tags?.map(tt => tt.tag_id) || [],
      };
    },
    customWhereCondition: getCustomWhereCondition(),
    onSuccess: async (operation, result, formData) => {
      if (formData.tagIds && (operation === 'create' || operation === 'update')) {
        try {
          const objects = formData.tagIds.map(tagId => ({
            team_id: result.id,
            tag_id: tagId,
          }));
          await hasuraClient.mutate(UPDATE_TEAM_TAGS, {
            team_id: result.id,
            objects: objects,
          });
        } catch (error) {
          console.error('Error updating team tags:', error);
        }
      }
    },
  });

  const fetchCompanies = useCallback(async () => {
    if (!isRootUser) return;
    
    try {
      const data = await hasuraClient.query(GET_COMPANIES_FOR_USER_FORM_QUERY);
      setCompanies(data.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      showNotification(t('companies.messages.fetchError') || 'Error fetching companies', 'error');
    }
  }, [isRootUser, t, showNotification]);

  const fetchGroups = useCallback(async () => {
    try {
      let data;
      if (isRootUser) {
        data = await hasuraClient.query(GET_ALL_GROUPS_FOR_FORM_QUERY);
      } else {
        data = await hasuraClient.query(GET_GROUPS_FOR_FORM_QUERY, {
          where: { company_id: { _eq: currentUser?.companyId } },
        });
      }
      setGroups(data.groups || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
      showNotification(t('groups.messages.fetchError') || 'Error fetching groups', 'error');
    }
  }, [currentUser?.companyId, isRootUser, t, showNotification]);

  const fetchUsers = useCallback(async () => {
    try {
      let data;
      if (isRootUser) {
        data = await hasuraClient.query(GET_ALL_USERS_QUERY);
      } else {
        data = await hasuraClient.query(GET_ALL_USERS_QUERY, {
          where: { company_id: { _eq: currentUser?.companyId } },
        });
      }
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      showNotification(t('users.messages.fetchError') || 'Error fetching users', 'error');
    }
  }, [currentUser?.companyId, isRootUser, t, showNotification]);

  useEffect(() => {
    if (currentUser?.id) {
      fetchCompanies();
      fetchGroups();
      fetchUsers();
    }
  }, [currentUser?.id, currentUser?.role, currentUser?.companyId]);

  return {
    ...dynamicHook,
    groups,
    companies,
    users,
    isRootUser,
  };
}; 